<template>
  <template v-if="isTotalMarker">
    <div
      v-for="cell in totalLabelCells"
      :key="cell.key"
      :data-total-path="dataPath"
      :data-cell="isOpened || undefined"
      :data-row="isOpened ? rowIndex : undefined"
      :data-col="isOpened ? cell.index : undefined"
      :class="totalCellClass"
      :style="!isOpened ? { display: 'none' } : undefined"
    >
      <div v-if="cell.text">
        {{ cell.text }} total
      </div>
    </div>
    <PivotValueCell
      v-for="cell in valueCells"
      :key="cell.key"
      :text="cell.text"
      :raw-value="cell.rawValue"
      :field="cell.field"
      :data-cell="isOpened"
      :hidden="!isOpened"
      :row="rowIndex"
      :col="cell.col"
      :total="true"
      :sign-colored="cell.signColored"
      @click="amount => $emit('click:value', cell.dimensions, amount)"
    />
  </template>
  <template v-else>
    <div
      v-for="cell in navLabelCells"
      :key="cell.key"
      :data-parent-path
      :data-path
      :data-level="level"
      :data-cell="!isNavHidden || undefined"
      :data-row="!isNavHidden ? rowIndex : undefined"
      :data-col="!isNavHidden ? cell.index : undefined"
      :class="rowParentClass"
      :style="isNavHidden ? { display: 'none' } : undefined"
    >
      <PivotRevealer
        v-if="cell.revealed"
        :opened="cell.opened"
        :path="cell.path"
        @click="event => handleReveal(event, cell)"
      />
      <PivotAssetLink
        v-if="cell.isLinkableNode && cell.text !== undefined"
        :name="cell.text"
        @click="cell.rawValue && $emit('click:text', cell.rawValue)"
      />
      <div
        v-else-if="cell.text !== undefined"
        :class="cell.clickable && CLICKABLE_TEXT_CLASS"
        @click="cell.clickable && cell.rawValue && $emit('click:text', cell.rawValue)"
      >
        {{ cell.text }}
      </div>
    </div>
    <PivotValueCell
      v-for="cell in valueCells"
      :key="cell.key"
      :text="cell.text"
      :raw-value="cell.rawValue"
      :field="cell.field"
      :parent-path="dataParentPath"
      :data-cell="!isNavHidden"
      :hidden="isNavHidden"
      :row="rowIndex"
      :col="cell.col"
      :sign-colored="cell.signColored"
      :is-clickable-value="isClickableValue"
      @click="amount => $emit('click:value', cell.dimensions, amount)"
    />
  </template>
</template>

<script setup lang="ts">
import { computed, inject } from 'vue'

import { PivotTableClickValueData, PivotTableData, PivotTableValueRenderers } from '@types'

import {
  CLICKABLE_NODES_PROVIDE_KEY,
  CLICKABLE_VALUES_PROVIDE_KEY,
  LINKABLE_NODES_PROVIDE_KEY,
  PATH_DELIMITER,
  RENDERERS_PROVIDE_KEY,
  SIGN_COLORED_FIELDS_PROVIDE_KEY,
  TOTAL_MARKER_KEY,
} from '@/components/DS/patterns/Pivot/utils/const'
import { PivotedDataParentPathEntry, addOpenedRow, getIsOpened, getValuesByColumns, removeOpenedRow, renderValue } from '@/components/DS/patterns/Pivot/utils/query'
import { cn } from '@/helpers/cn'

import PivotAssetLink from './primitives/PivotAssetLink.vue'
import PivotRevealer from './primitives/PivotRevealer.vue'
import PivotValueCell from './primitives/PivotValueCell.vue'

const CLICKABLE_TEXT_CLASS = 'underline hover:no-underline underline-offset-2 decoration-muted-foreground cursor-pointer'

// content-visibility/contain-intrinsic-height: see PivotValueCell's comment — keeps every row
// mounted, letting the browser skip layout/paint for off-screen rows.
const BASE_CELL_CLASS = 'py-3.5 px-4 relative flex items-center justify-end font-ds text-foreground bg-card text-sm sm:text-xs text-right overflow-hidden [content-visibility:auto] [contain-intrinsic-height:auto_32px] data-row-hovered:bg-[#fafaff] data-row-hovered:dark:bg-[#151520] cursor-cell'
const STICKY_LABEL_CLASS = 'sticky left-0 justify-start text-left z-10'

// Classic's nav label cells sit on the same plain card background as the rest of the row.
const rowParentClass = cn(BASE_CELL_CLASS, STICKY_LABEL_CLASS)
// A node's own trailing total row — a light neutral wash, bolded.
const totalCellClass = cn(BASE_CELL_CLASS, STICKY_LABEL_CLASS, 'font-bold bg-[#fafaff] dark:bg-[#151520]')

type LoadChildren = (parentPath: PivotedDataParentPathEntry[], pathKey: string) => Promise<PivotTableData[]>

type Props = {
  item: PivotTableData
  columns: PivotTableData[]
  colsList?: string[]
  rowsList?: string[]
  valuesList?: string[]
  /** Depth where this row's label first diverges from its prior sibling — see `PivotContent`'s `rowLabelStartDepth`. */
  labelStartDepth?: number
  /** This row's `data-row` — the `v-for` index over `PivotContent`'s `data`. */
  rowIndex: number
}

type Emits = {
  'click:text': [data: string]
  'click:value': [data: PivotTableClickValueData, amount: number]
}

const { item, columns, colsList = [], rowsList = [], valuesList = [], labelStartDepth = 0 } = defineProps<Props>()
defineEmits<Emits>()

const openedRows = inject<{ value: string[] }>('openedRows')
const loadChildren = inject<LoadChildren | undefined>('loadChildren', undefined)
const childrenCache = inject<Map<string, PivotTableData[]> | undefined>('childrenCache', undefined)
const loadingPaths = inject<Set<string> | undefined>('loadingPaths', undefined)
const linkableNodes = inject(LINKABLE_NODES_PROVIDE_KEY, [])
const clickableNodes = inject(CLICKABLE_NODES_PROVIDE_KEY, [])
const renderers = inject(RENDERERS_PROVIDE_KEY, computed<PivotTableValueRenderers>(() => ({})))
const signColoredFields = inject(SIGN_COLORED_FIELDS_PROVIDE_KEY, [])
const isClickableValue = inject(CLICKABLE_VALUES_PROVIDE_KEY, false)

const isTotalMarker = computed(() => !!item[TOTAL_MARKER_KEY])

const path = computed(() => rowsList.map(row => item[row]).filter(Boolean))
const level = computed(() => path.value.length - 1)

const dataPath = computed(() => level.value >= 0 ? path.value.join(PATH_DELIMITER) : undefined)
const dataParentPath = computed(() => level.value > 0 ? path.value.slice(0, level.value).join(PATH_DELIMITER) : undefined)

const isOpened = computed(() => getIsOpened(dataPath.value, openedRows))
const isParentOpened = computed(() => getIsOpened(dataParentPath.value, openedRows))

const isLeaf = computed(() => level.value >= rowsList.length - 1)
const isNavHidden = computed(() => (level.value > 0 && !isParentOpened.value) || (isOpened.value && !isLeaf.value))

const resultPathFor = (i: number) => path.value.slice(0, i + 1).join(PATH_DELIMITER)

type LabelCellSpec = {
  key: string
  index: number
  text?: string
  rawValue?: string
  isLinkableNode?: boolean
  clickable?: boolean
  revealed?: boolean
  opened?: boolean
  path?: string
}

// One label cell per row level — Classic shows the breadcrumb as sibling columns.
// A sibling group's first row shows the full chain down to `labelStartDepth`; repeats blank unchanged levels.
const navLabelCells = computed<LabelCellSpec[]>(() => {
  return rowsList.map((row, i) => {
    const rowValue = item[row]
    const showLabel = i >= labelStartDepth
    const isRevealed = showLabel && !!rowValue && i !== rowsList.length - 1
    const cellPath = resultPathFor(i)
    return {
      key: `nav:${i}`,
      index: i,
      text: showLabel ? renderValue(rowValue, row, renderers.value, 'header-row', isRevealed) : undefined,
      rawValue: rowValue,
      isLinkableNode: linkableNodes.includes(row),
      clickable: showLabel && clickableNodes.includes(row),
      revealed: isRevealed,
      opened: getIsOpened(cellPath, openedRows),
      path: cellPath,
    }
  })
})

const totalLabelCells = computed<LabelCellSpec[]>(() => {
  return rowsList.map((row, i) => ({
    key: `total:${i}`,
    index: i,
    text: i === level.value ? renderValue(item[row], row, renderers.value, 'header-row') : undefined,
  }))
})

type ValueCellSpec = { key: string, text: string, rawValue: unknown, field: string, dimensions: PivotTableClickValueData, signColored: boolean, col: number }

// Same role as `path`, but field-keyed for a cell's drill-down filter. Reads `item` (not `path`),
// so it also works for a total-marker row, which copies its parent's dimensions onto `item`.
const rowDimensions = computed<PivotTableClickValueData>(() => Object.fromEntries(
  rowsList.filter(row => item[row] !== undefined).map(row => [row, item[row]]),
))

const valueCells = computed<ValueCellSpec[]>(() => {
  const cells: ValueCellSpec[] = []
  const valuesByColumns = getValuesByColumns(item, columns, colsList, valuesList)
  let col = rowsList.length
  for (const { value, valueField, path } of valuesByColumns) {
    const colDimensions = Object.fromEntries(colsList.map((colField, index) => [colField, path[index]]))
    cells.push({
      key: `col:${path.join(PATH_DELIMITER)}`,
      text: renderValue(value, valueField, renderers.value),
      rawValue: value,
      field: valueField,
      dimensions: { ...rowDimensions.value, ...colDimensions },
      signColored: signColoredFields.includes(valueField),
      col: col++,
    })
  }
  for (const field of valuesList) {
    cells.push({
      key: `total:${field}`,
      text: renderValue(item[field], field, renderers.value),
      rawValue: item[field],
      field,
      dimensions: rowDimensions.value,
      signColored: signColoredFields.includes(field),
      col: col++,
    })
  }
  return cells
})

const handleReveal = async (event: MouseEvent, cell: LabelCellSpec) => {
  event.stopPropagation()
  event.preventDefault()
  const cellPath = cell.path
  if (!cellPath || loadingPaths?.has(cellPath)) return
  if (getIsOpened(cellPath, openedRows)) {
    removeOpenedRow(cellPath, openedRows)
    return
  }
  addOpenedRow(cellPath, openedRows)
  // children already loaded, or nothing to lazily load
  if (!loadChildren || childrenCache?.has(cellPath)) return
  const parentPath = rowsList.slice(0, cell.index + 1).map((field, idx) => ({ field, value: path.value[idx] }))
  const revealerEl = (event.target as HTMLElement)?.closest('[data-revealer]')
  revealerEl?.setAttribute('data-loading', 'true')
  try {
    await loadChildren(parentPath, cellPath)
  } finally {
    revealerEl?.removeAttribute('data-loading')
  }
}
</script>
